import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { MAX_CART_QUANTITY } from "@tarawa/shared";
import { api, ApiError } from "./api";
import { useAuth } from "./auth";
import type { Quote } from "./types";

interface Line {
  productId: string;
  quantity: number;
  /** Chosen weight (grams) for products sold by weight; absent = the base weight */
  weightGrams?: number | null;
}

interface PackLine {
  packId: string;
  quantity: number;
}

const GUEST_KEY = "tw_cart";
const GUEST_PACKS_KEY = "tw_cart_packs";
const COUPON_KEY = "tw_coupon";

function readGuestPacks(): PackLine[] {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_PACKS_KEY) ?? "[]") as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((l): l is PackLine => typeof l?.packId === "string" && Number.isInteger(l?.quantity) && l.quantity > 0)
      .map((l) => ({ packId: l.packId, quantity: Math.min(l.quantity, MAX_CART_QUANTITY) }));
  } catch {
    return [];
  }
}
function writeGuestPacks(lines: PackLine[]) {
  try {
    localStorage.setItem(GUEST_PACKS_KEY, JSON.stringify(lines));
  } catch {
    /* ignore */
  }
}

function readGuest(): Line[] {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_KEY) ?? "[]") as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((l): l is Line => typeof l?.productId === "string" && Number.isInteger(l?.quantity) && l.quantity > 0)
      .map((l) => ({
        productId: l.productId,
        quantity: Math.min(l.quantity, MAX_CART_QUANTITY),
        ...(Number.isInteger(l.weightGrams) && l.weightGrams! > 0 ? { weightGrams: l.weightGrams } : {}),
      }));
  } catch {
    return [];
  }
}
function writeGuest(lines: Line[]) {
  try {
    localStorage.setItem(GUEST_KEY, JSON.stringify(lines));
  } catch {
    /* ignore */
  }
}

interface CartApi {
  lines: Line[];
  packLines: PackLine[];
  /** True when the cart has neither products nor packs. */
  isEmpty: boolean;
  /** Number of distinct products and packs in the cart (quantities are not summed). */
  count: number;
  ready: boolean;
  quote: Quote | undefined;
  quoteLoading: boolean;
  quoteFetching: boolean;
  couponCode: string;
  setCouponCode: (code: string) => void;
  quantityOf: (productId: string) => number;
  /** Chosen weight of a product sold by weight (undefined = its base weight). */
  weightOf: (productId: string) => number | null | undefined;
  /** Sets a quantity (0 removes) and, when given, the chosen weight. Validated by the backend. */
  setQuantity: (productId: string, quantity: number, weightGrams?: number) => Promise<void>;
  add: (productId: string, quantity?: number) => Promise<void>;
  packQuantityOf: (packId: string) => number;
  /** Sets the quantity of a pack (0 removes). Validated by the backend. */
  setPackQuantity: (packId: string, quantity: number) => Promise<void>;
  removeUnavailable: () => Promise<void>;
  /** Reloads the account cart from the server (after reorder / order placement). */
  reload: () => Promise<void>;
}

const CartContext = createContext<CartApi | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { customer, isLoading: authLoading } = useAuth();
  const [lines, setLines] = useState<Line[]>(() => readGuest());
  const [packLines, setPackLines] = useState<PackLine[]>(() => readGuestPacks());
  const [ready, setReady] = useState(false);
  const [couponCode, setCouponState] = useState<string>(() => {
    try {
      return sessionStorage.getItem(COUPON_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const packLinesRef = useRef(packLines);
  packLinesRef.current = packLines;
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const customerId = customer?.id ?? null;

  const loadServerCart = useCallback(async () => {
    const res = await api<{ quote: Quote }>("/cart");
    setLines(res.quote.lines.map((l) => ({ productId: l.productId, quantity: l.quantity, weightGrams: l.weightGrams ?? null })));
    setPackLines((res.quote.packLines ?? []).map((l) => ({ packId: l.packId, quantity: l.quantity })));
  }, []);

  // Sign-in: merge the guest cart into the account cart. Sign-out: start empty.
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      if (customerId) {
        const guest = readGuest();
        const guestPacks = readGuestPacks();
        if (guest.length || guestPacks.length) {
          await api("/cart/merge", { method: "POST", body: { items: guest, packs: guestPacks } }).catch(() => undefined);
          writeGuest([]);
          writeGuestPacks([]);
        }
        await loadServerCart().catch(() => undefined);
      } else {
        setLines(readGuest());
        setPackLines(readGuestPacks());
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, authLoading, loadServerCart]);

  const setCouponCode = useCallback((code: string) => {
    const c = code.trim().toUpperCase();
    setCouponState(c);
    try {
      if (c) sessionStorage.setItem(COUPON_KEY, c);
      else sessionStorage.removeItem(COUPON_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const quoteQuery = useQuery({
    queryKey: ["quote", lines, packLines, couponCode],
    queryFn: () =>
      api<{ quote: Quote }>("/cart/quote", {
        method: "POST",
        body: { items: lines, packs: packLines, couponCode: couponCode || null },
      }).then((r) => r.quote),
    enabled: ready && (lines.length > 0 || packLines.length > 0),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const setQuantity = useCallback(
    async (productId: string, quantity: number, weightGrams?: number) => {
      const q = Math.max(0, Math.min(MAX_CART_QUANTITY, Math.floor(quantity)));
      const previous = linesRef.current;
      const exists = previous.some((l) => l.productId === productId);
      const weight = weightGrams !== undefined ? { weightGrams } : {};
      const next =
        q === 0
          ? previous.filter((l) => l.productId !== productId)
          : exists
            ? previous.map((l) => (l.productId === productId ? { ...l, quantity: q, ...weight } : l))
            : [...previous, { productId, quantity: q, ...weight }];

      linesRef.current = next;
      setLines(next); // optimistic
      try {
        if (customerId) {
          // Serialize writes so rapid taps reach the server in order.
          const op = queueRef.current.then(() =>
            api(`/cart/items/${productId}`, { method: "PUT", body: { quantity: q, ...weight } }),
          );
          queueRef.current = op.catch(() => undefined);
          await op;
        } else {
          if (q > 0 && !exists) {
            // Guests: ask the backend whether the product can be sold before adding it.
            const res = await api<{ quote: Quote }>("/cart/quote", {
              method: "POST",
              body: { items: [{ productId, quantity: q, ...weight }] },
            });
            if (!res.quote.lines[0]?.available) throw new ApiError(409, "PRODUCT_UNAVAILABLE");
          }
          writeGuest(next);
        }
      } catch (err) {
        linesRef.current = previous;
        setLines(previous);
        if (!customerId) writeGuest(previous);
        throw err;
      }
    },
    [customerId],
  );

  const setPackQuantity = useCallback(
    async (packId: string, quantity: number) => {
      const q = Math.max(0, Math.min(MAX_CART_QUANTITY, Math.floor(quantity)));
      const previous = packLinesRef.current;
      const exists = previous.some((l) => l.packId === packId);
      const next =
        q === 0
          ? previous.filter((l) => l.packId !== packId)
          : exists
            ? previous.map((l) => (l.packId === packId ? { ...l, quantity: q } : l))
            : [...previous, { packId, quantity: q }];

      packLinesRef.current = next;
      setPackLines(next); // optimistic
      try {
        if (customerId) {
          const op = queueRef.current.then(() => api(`/cart/packs/${packId}`, { method: "PUT", body: { quantity: q } }));
          queueRef.current = op.catch(() => undefined);
          await op;
        } else {
          if (q > 0 && !exists) {
            const res = await api<{ quote: Quote }>("/cart/quote", { method: "POST", body: { items: [], packs: [{ packId, quantity: q }] } });
            if (!res.quote.packLines?.[0]?.available) throw new ApiError(409, "PACK_UNAVAILABLE");
          }
          writeGuestPacks(next);
        }
      } catch (err) {
        packLinesRef.current = previous;
        setPackLines(previous);
        if (!customerId) writeGuestPacks(previous);
        throw err;
      }
    },
    [customerId],
  );

  const value = useMemo<CartApi>(() => {
    const quantityOf = (id: string) => lines.find((l) => l.productId === id)?.quantity ?? 0;
    const weightOf = (id: string) => lines.find((l) => l.productId === id)?.weightGrams;
    const packQuantityOf = (id: string) => packLines.find((l) => l.packId === id)?.quantity ?? 0;
    const isEmpty = lines.length === 0 && packLines.length === 0;
    return {
      lines,
      packLines,
      isEmpty,
      // Badge shows distinct products and packs, not units: quantity changes leave it unchanged.
      count: new Set(lines.map((l) => l.productId)).size + packLines.length,
      ready,
      quote: isEmpty ? undefined : quoteQuery.data,
      quoteLoading: quoteQuery.isLoading,
      quoteFetching: quoteQuery.isFetching,
      couponCode,
      setCouponCode,
      quantityOf,
      weightOf,
      setQuantity,
      add: (id, qty = 1) => setQuantity(id, quantityOf(id) + qty),
      packQuantityOf,
      setPackQuantity,
      removeUnavailable: async () => {
        const unavailable = quoteQuery.data?.lines.filter((l) => !l.available) ?? [];
        for (const l of unavailable) await setQuantity(l.productId, 0);
        const unavailablePacks = quoteQuery.data?.packLines?.filter((l) => !l.available) ?? [];
        for (const l of unavailablePacks) await setPackQuantity(l.packId, 0);
      },
      reload: async () => {
        if (customerId) await loadServerCart();
        else {
          setLines(readGuest());
          setPackLines(readGuestPacks());
        }
      },
    };
  }, [lines, packLines, ready, quoteQuery.data, quoteQuery.isLoading, quoteQuery.isFetching, couponCode, setCouponCode, setQuantity, setPackQuantity, customerId, loadServerCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartApi {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
